import React, { useState, useCallback, useEffect, useRef } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import VariableListItem from './VariableListItem';
import './MenuDisplay.scss';

import { sortOrdersFunction, FormattedSystemType } from './Bulletin.helper';
import { BulletinCategory, BulletinEntry } from './types';
import { useBulletin, useBulletinResponse } from '../../context/bulletin';
import FlagModal from '../ContentBox/FlagModal';
import SendMessageButton from './SendMessageButton';
import CategoryFrequency from './CategoryFrequency';

interface MenuDisplayProps {
  userIsAuthenticated: boolean;
  entryTreeKey: string | null;
  entryType: string;
}

const MenuDisplay = ({
  userIsAuthenticated,
  entryTreeKey,
  entryType,
}: MenuDisplayProps) => {
  const [categoryToFlag, setCategoryToFlag] = useState<string | undefined>();
  const { isLatestBulletin } = useBulletin();
  const { systems, bulletin } = useBulletinResponse();

  // keep list of active categories per systems
  const [activeCategories, setActiveCategories] = useState<
    Record<string, AccordionEventKey>
  >(systems.reduce((obj, system) => ({ ...obj, [system.code]: [] }), {}));
  const [formatedSystems, setFormatedSystems] =
    useState<Array<FormattedSystemType>>(systems);
  const previousActiveCategories = useRef<Record<
    string,
    AccordionEventKey
  > | null>(null);

  const [flaggedCategories, setFlaggedCategories] = useState(
    systems
      .reduce(
        (prev, curr) => [...prev, ...curr.categories],
        [] as Array<BulletinCategory>,
      )
      .filter(c => c.flag)
      .map(c => c.path),
  );

  const getGroupSeenIconStatus = useCallback(
    (entries: Array<BulletinEntry>) => {
      const unseenList = entries.filter(entry => entry.unseen);
      if (unseenList.length === entries.length) {
        return {
          icon: 'icon-circle-dash',
          title: 'All variables are unseen',
        };
      }
      if (unseenList.length > 0) {
        return {
          icon: 'icon-incomplete',
          title: 'Variables are partially seen',
        };
      }
      return {
        icon: 'icon-check',
        title: 'Everything is seen',
      };
    },
    [],
  );

  // keep previous active categories
  useEffect(() => {
    previousActiveCategories.current = activeCategories;
  }, [activeCategories]);

  // update actives categories if systems | entryId | entryType change
  useEffect(() => {
    const formatedSys = systems.sort(sortOrdersFunction).map(system => {
      const orderedCategories = system.categories.sort(sortOrdersFunction);
      // open accordion only for the category containing the current entry id
      // or if none are selected, for the first category of the first displayed system
      const currentCategoryName = orderedCategories.find(category =>
        category.entries.some(
          entry => entry.tree_key === entryTreeKey && entry.type === entryType,
        ),
      )?.name;

      return {
        ...system,
        orderedCategories,
        activeCategories: currentCategoryName ? [currentCategoryName] : [],
      };
    });

    const activeCategoriesBySystem = formatedSys.reduce((obj, cur) => {
      // merge current active categories with previously selected categories
      // to keep the previous user selection, if any
      const activeCats = [
        ...new Set([
          ...cur.activeCategories,
          ...(previousActiveCategories.current?.[cur.code] ?? []),
        ]),
      ];
      return {
        ...obj,
        [cur.code]: activeCats,
      };
    }, {});

    setActiveCategories(activeCategoriesBySystem);
    setFormatedSystems(formatedSys);
  }, [systems, entryTreeKey, entryType]);

  const canSendMessages =
    userIsAuthenticated && bulletin.validation_enabled && !bulletin.isValidated;

  return (
    <>
      {formatedSystems.map(system => (
        <div
          key={system.code}
          className={`systems-item ${
            !isLatestBulletin ? 'olderBulletin' : 'newestBulletin'
          }`}
        >
          <h2 className="systems-item__title">{system.name}</h2>
          <Accordion
            activeKey={activeCategories[system.code]}
            onSelect={eventKey => {
              setActiveCategories(prevState => ({
                ...prevState,
                [system.code]: eventKey,
              }));
            }}
            alwaysOpen
            flush
          >
            {system?.orderedCategories?.map((category, categoryIndex) => {
              const isCategoryEmpty = category.entries.every(e => e.outdated);
              return (
                <Accordion.Item
                  key={`${category.name}-${categoryIndex}`}
                  eventKey={category.name}
                >
                  <Accordion.Header className={isCategoryEmpty ? 'empty' : ''}>
                    <span className="accordion-button-span">
                      {userIsAuthenticated && !isCategoryEmpty && (
                        <span
                          className={`icon ${
                            getGroupSeenIconStatus(category.entries).icon
                          }`}
                          title={getGroupSeenIconStatus(category.entries).title}
                        />
                      )}
                      <span className="category-title">
                        <span className="label">{category.name}</span>
                        {isLatestBulletin && (
                          <CategoryFrequency category={category} />
                        )}
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {canSendMessages && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          paddingBottom: 5,
                        }}
                      >
                        <SendMessageButton
                          alreadySent={flaggedCategories.some(
                            path => path === category.path,
                          )}
                          onClick={() => setCategoryToFlag(category.path)}
                          type="category"
                          itemId={category.path}
                        />
                      </div>
                    )}
                    {category.entries.map((entry, entryIndex) => (
                      <VariableListItem
                        key={`${entry.tree_key}-${entryIndex}`}
                        entry={entry}
                        userIsAuthenticated={userIsAuthenticated}
                      />
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      ))}
      <FlagModal
        show={categoryToFlag !== undefined}
        type="category"
        id={categoryToFlag ?? ''}
        onCancel={() => setCategoryToFlag(undefined)}
        onSuccess={() => {
          setFlaggedCategories(prev => [...prev, categoryToFlag ?? '']);
          setCategoryToFlag(undefined);
        }}
      />
    </>
  );
};

export default MenuDisplay;
